import React, { useState, useMemo, useCallback, memo } from "react"
import ExcelJS from "exceljs"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Label,
  Card,
  CardBody,
  UncontrolledTooltip,
} from "reactstrap"
import excelIcon from "../../../src/assets/images/excel.svg"

const CheckboxItem = memo(function CheckboxItem({ header, checked, onToggle }) {
  return (
    <Label
      check
      className="d-flex align-items-center border rounded p-2 bg-light"
      style={{ cursor: "pointer", userSelect: "none" }}
    >
      <input
        type="checkbox"
        checked={checked}
        onClick={() => onToggle(header)}
        className="me-2"
        style={{ cursor: "pointer" }}
        readOnly
      />
      <span className="text-capitalize small">{header.replace(/_/g, " ")}</span>
    </Label>
  )
})

const ExcelDownload = ({ leads = [], title = "Download Data as Excel" }) => {
  const moment = require("moment-timezone")
  const [modal, setModal] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState([])

  const uniqueId = useMemo(() => Math.random().toString(36).substring(2, 8), [])

  const leadsData = Array.isArray(leads) ? leads : []

  const availableHeaders = useMemo(() => {
    if (leadsData.length === 0) return []
    return Object.keys(leadsData[0]).filter(
      key =>
        !["id", "_id", "__v"].includes(key) &&
        typeof leadsData[0][key] !== "function"
    )
  }, [leadsData])

  const toggleModal = () => setModal(!modal)

  const handleColumnChange = useCallback(
    column => {
      setSelectedColumns(prev => {
        const newSet = new Set(prev)
        if (newSet.has(column)) newSet.delete(column)
        else newSet.add(column)
        return Array.from(newSet)
      })
    },
    [setSelectedColumns]
  )

  const toggleAllColumns = useCallback(() => {
    setSelectedColumns(prev =>
      prev.length === availableHeaders.length ? [] : [...availableHeaders]
    )
  }, [availableHeaders])

  const safeValue = value => {
    if (value === null || value === undefined) return ""
    if (typeof value === "object") return JSON.stringify(value)
    return value
  }

  const downloadExcel = async () => {
    if (selectedColumns.length === 0) {
      alert("Please select at least one column to download.")
      return
    }

    const currentDate = moment().tz("Asia/Karachi").format("YYYY-MM-DD")
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet("Exported Data")

    worksheet.addRow(selectedColumns)
    const headerRow = worksheet.getRow(1)
    headerRow.eachCell(cell => {
      cell.font = { bold: true }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      }
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      }
    })

    leadsData.forEach(item => {
      const row = selectedColumns.map(header => safeValue(item[header]))
      worksheet.addRow(row)
    })

    selectedColumns.forEach((header, index) => {
      const maxLength = Math.max(
        header.length,
        ...leadsData.map(i => safeValue(i[header]).toString().length)
      )
      worksheet.getColumn(index + 1).width = Math.min(
        Math.max(maxLength + 2, 10),
        60
      )
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: "application/octet-stream" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `${currentDate}-Exported-Data.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toggleModal()
  }

  const openModal = () => {
    setSelectedColumns([...availableHeaders])
    setModal(true)
  }

  return (
    <div>
      {/* Excel Icon */}
      <div
        style={{
          cursor: "pointer",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          padding: "8px",
          backgroundColor: "#f8f9fa",
          transition: "all 0.2s ease-in-out",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
        onClick={openModal}
        id={`excelIcon-${uniqueId}`}
        onMouseEnter={e => {
          e.target.style.backgroundColor = "#e9ecef"
          e.target.style.transform = "scale(1.05)"
        }}
        onMouseLeave={e => {
          e.target.style.backgroundColor = "#f8f9fa"
          e.target.style.transform = "scale(1)"
        }}
      >
        <img
          src={excelIcon}
          alt="Excel Download"
          style={{ width: "24px", height: "24px" }}
        />
      </div>

      <UncontrolledTooltip placement="top" target={`excelIcon-${uniqueId}`}>
        {title}
      </UncontrolledTooltip>

      {/* Modal */}
      <Modal
        key={uniqueId}
        isOpen={modal}
        toggle={toggleModal}
        size="xl"
        centered
        scrollable
      >
        <ModalHeader
          toggle={toggleModal}
          style={{
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <h5 className="modal-title mb-0">
            <i className="fas fa-file-excel mr-2 text-success"></i> Select
            Columns to Download
          </h5>
        </ModalHeader>

        <ModalBody className="p-4">
          {/* Column Selector */}
          <Card className="border-0 shadow-sm mb-4">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">
                  <i className="fas fa-columns mr-2 text-primary"></i> Available
                  Columns
                </h6>
                <Button
                  size="sm"
                  color="info"
                  onClick={toggleAllColumns}
                  className="px-3"
                >
                  {selectedColumns.length === availableHeaders.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "6px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #eee",
                  padding: "8px",
                  borderRadius: "8px",
                }}
              >
                {availableHeaders.map(header => (
                  <CheckboxItem
                    key={header}
                    header={header}
                    checked={selectedColumns.includes(header)}
                    onToggle={handleColumnChange}
                  />
                ))}
              </div>

              <small className="text-muted mt-2 d-block">
                {selectedColumns.length} of {availableHeaders.length} selected
              </small>
            </CardBody>
          </Card>

          {/* Data Preview */}
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <h6 className="card-title mb-3 p-3 border-bottom">
                <i className="fas fa-table mr-2 text-info"></i> Data Preview
              </h6>
              <div
                style={{ maxHeight: "500px", overflowY: "auto" }}
                className="p-3"
              >
                <Table striped bordered hover responsive className="mb-0">
                  <thead className="thead-light sticky-top">
                    <tr>
                      {selectedColumns.map(col => (
                        <th key={col}>
                          {col.replace(/_/g, " ").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leadsData.slice(0, 20).map((item, index) => (
                      <tr key={index}>
                        {selectedColumns.map(col => (
                          <td key={col}>{safeValue(item[col]) || "-"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="bg-light border-top">
          <Button color="secondary" onClick={toggleModal} className="me-auto">
            Cancel
          </Button>
          <Button
            color="success"
            onClick={downloadExcel}
            disabled={selectedColumns.length === 0}
          >
            Download Excel ({selectedColumns.length})
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ExcelDownload
